import { useEffect, useRef } from 'react'
import { TrustBar, TrustItem } from '@azoya-spa/ui'
import '@azoya-spa/ui/styles.css'
import { trustItemLabel } from '@azoya-spa/contracts'
import { useSiteContent } from '../../hooks/useSiteContent'
import repli from '../../api/fallback/trustBar.json'

/**
 * Fait apparaître la bande au défilement, comme le fait la page pour ses
 * autres sections — mais sans dépendre du passage que la page a déjà fait.
 *
 * Les deux pages observent `[data-reveal]` UNE FOIS, au montage. Or cette
 * bande-ci peut n'exister qu'après : si la copie mémorisée du navigateur est
 * vide (la gérante avait retiré tous ses arguments) et que l'API en renvoie
 * ensuite, le conteneur apparaît une fois l'observation passée. Il resterait
 * alors à `opacity: 0` pour toujours — présent dans le DOM, invisible à
 * l'écran, et rien ne le signalerait.
 *
 * Mêmes réglages que les deux pages : la bande apparaît au même moment que ses
 * voisines, qu'elle ait été révélée par leur observateur ou par celui-ci.
 */
function useApparition(actif) {
  const ref = useRef(null)

  useEffect(() => {
    const element = ref.current
    // Déjà révélé par l'observateur de la page : rien à refaire.
    if (!element || element.classList.contains('is-visible')) return

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer.unobserve(entry.target)
        }
      })
    // Seuil 0 : un pourcentage du bloc serait hors d'atteinte pour un bloc
    // plus haut que l'écran, qui resterait invisible (voir OffresAbonnement).
    }, { threshold: 0, rootMargin: '0px 0px -40px 0px' })

    observer.observe(element)
    return () => observer.disconnect()
  }, [actif])

  return ref
}

/**
 * La bande d'arguments de confiance, affichée sous le hero de la page
 * d'accueil et en bas de « Offres & Abonnements ».
 *
 * Un seul composant et un seul contenu pour les deux pages. Avant, chacune
 * avait le sien : l'accueil l'écrivait en dur avec des icônes react-icons, les
 * offres le tiraient de leurs textes avec des caractères. Les deux avaient
 * déjà divergé — quatre icônes différentes, et « sur plus de 200 avis » d'un
 * côté contre « ...avis clients » de l'autre. C'est cette divergence-là que ce
 * fichier ferme : il n'y a plus deux endroits où corriger une faute.
 *
 * Le conteneur est rendu ici, avec sa classe et son `data-reveal` : c'est ce
 * qui permet à la bande de disparaître VRAIMENT quand la gérante retire tous
 * ses arguments. Rendu par la page, il resterait un blanc de la hauteur d'une
 * section, sans rien dedans.
 */
export default function BandeauDeConfiance({ className }) {
  const bandeau = useSiteContent('trust-bar', repli)
  const visible = bandeau.items.length > 0
  // Appelé avant tout retour anticipé : un hook ne peut pas être conditionnel.
  const ref = useApparition(visible)

  if (!visible) return null

  return (
    // `data-reveal` reste posé : c'est le marqueur que l'observateur de la page
    // reconnaît, et il révèle cette bande comme ses voisines dans le cas
    // courant. useApparition n'est que le filet du cas tardif ci-dessus.
    <div ref={ref} className={className} data-reveal>
      <TrustBar>
        {bandeau.items.map((item, index) => (
          // Index en clé : deux arguments peuvent porter le même texte le temps
          // d'une saisie, et la liste n'est jamais réordonnée à l'affichage.
          <TrustItem key={index} icon={item.icon}>
            {trustItemLabel(item)}
          </TrustItem>
        ))}
      </TrustBar>
    </div>
  )
}
